import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { SelectKVPControl } from "@/Common/Components/Inputs/SelectKVPControl";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { Patient } from "@/Common/Models/Patient/Patient";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { IExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { FileTypeWorkSafeBcForm } from "@/ExportableFile/Models/FileTypes/FileTypeWorkSafeBcForm";
import { IFaxPrintExportableFileConfig } from "@/ExportableFile/Models/IFaxPrintExportableFileConfig";
import { FormContainer } from "@/Patient/Components/MedicalNote/Forms/FormContainer";
import { Encounter } from "@/Patient/Models/MedicalSummary/Encounter";
import { WorkSafeBcFormType } from "./Constants/WorkSafeBcFormType";
import { WorkSafeBcFormLight } from "./Models/WorkSafeBcFormLight";
import { WorkSafeBcPhysicianReportModel } from "./Models/WorkSafeBcPhysicianReportModel";
import { WorkSafeBcWorkerModel } from "./Models/WorkSafeBcWorkerModel";
import { CreateWorkSafeBcFormResponse } from "./ResponseModels/CreateWorkSafeBcFormResponse";
import { SaveWorkSafeBcPhysicianReportFormResponse } from "./ResponseModels/SaveWorkSafeBcPhysicianReportFormResponse";
import { WorkSafeBcPhysicianReportForm } from "./WorkSafeBcPhysicianReportForm";

interface WorkSafeBcProps {
    isReadOnly?: boolean;
    isEditMode: boolean;
    patient: Patient;
    encounter: Encounter;
    model: WorkSafeBcFormLight[];
}

interface WorkSafeBcState {
    newFormState?: {
        formtype: WorkSafeBcFormType,
        selectedFormId: string
    } | null;

    existingForms: WorkSafeBcFormLight[];
}

class WorkSafeBcController extends BaseReactComponent<WorkSafeBcProps, WorkSafeBcState> {
    private workSafeBcPhysicianReportForm: WorkSafeBcPhysicianReportForm;

    public constructor(props: WorkSafeBcProps) {
        super(props);
        this.state = this.initState(props);
    }

    public componentDidUpdate(prevProps: WorkSafeBcProps): void {
        const exitedEditMode = prevProps.isEditMode !== this.props.isEditMode && this.props.isEditMode === false;
        const modelChanged = prevProps.model !== this.props.model;

        if (exitedEditMode || modelChanged) {
            this.setState(this.initState(this.props));
        }
    }

    public async saveForm(): Promise<void> {
        const form = this.state.existingForms.firstOrDefault(p => p.EncounterId === this.props.encounter.Id);

        // The form is already saved on componentWillUnmount (when collapsed).
        if (form?.isExtended)
            await this.workSafeBcPhysicianReportForm?.saveForm();
    }

    public render(): JSX.Element {
        const encounterHasForm = this.encounterHasForm();
        const showFormCreationDropdown = this.state.newFormState && this.state.existingForms.any();

        return (
            <tr>
                <td className="c1" style={{ verticalAlign: encounterHasForm ? "top" : "middle", paddingBottom: "10px" }}>
                    <strong>{cacheStore.GetString("WorkSafeBc_WorkSafeBcTitleLabel")}</strong>
                </td>
                <td className="c2" style={{ verticalAlign: encounterHasForm ? "top" : "middle", paddingBottom: "10px" }}>
                    <div className="tb-row tb">
                        <div className="add-btn-column" style={{ verticalAlign: "middle" }}>
                            {!encounterHasForm && this.props.isEditMode &&
                                <a onClick={() => this.onFormSelectionClick(WorkSafeBcFormType.Form8)}>[+]</a>
                            }
                        </div>
                        <div className="note-items-column">
                            {!encounterHasForm && (
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    {this.props.isEditMode && (
                                        <div>
                                            <a onClick={() => this.onFormSelectionClick(WorkSafeBcFormType.Form8)}>{cacheStore.GetString("WorkSafeBc_Form8Label")}</a>
                                            <span style={{ marginLeft: "10px", marginRight: "10px" }}>|</span>
                                            <a onClick={() => this.onFormSelectionClick(WorkSafeBcFormType.Form11)}>{cacheStore.GetString("WorkSafeBc_Form11Label")}</a>
                                        </div>
                                    )}
                                    {showFormCreationDropdown && (
                                        <SelectKVPControl
                                            Value={this.state.newFormState.selectedFormId}
                                            AvailableValues={this.createNewFormOptions}
                                            style={{ maxWidth: "360px", margin: "0 10px" }}
                                            Callback={key => this.mutateState(s => s.newFormState.selectedFormId = key)}
                                        />
                                    )}
                                    {showFormCreationDropdown &&
                                        <button className="btn btn-primary" onClick={() => this.onCreateNewForm()}>{cacheStore.GetString("CreateLabel")}</button>
                                    }
                                </div>
                            )}
                            {this.renderForm()}
                        </div>
                    </div>
                </td>
            </tr>
        );
    }

    private initState(props: WorkSafeBcProps): WorkSafeBcState {
        return {
            existingForms: props.model,
            newFormState: null
        };
    }

    private onFormSelectionClick(formType: WorkSafeBcFormType): void {
        this.mutateState(
            s => {
                s.newFormState = {
                    formtype: formType,
                    selectedFormId: GuidHelper.Empty()
                };
            },
            () => {
                if (!this.state.existingForms?.any())
                    this.onCreateNewForm();
            }
        );
    }

    private renderForm(): JSX.Element {
        const form = this.state.existingForms.firstOrDefault(p => p.EncounterId === this.props.encounter.Id);

        if (!form)
            return null;

        return (
            <FormContainer
                key={`WorkSafeBcForm_${form.Id}`}
                containerId={`workSafeBcForm_${form.Id}`}
                isExtended={form.isExtended}
                patient={this.props.patient}
                header={(
                    <div className="worksafe-bc-header margin-lg-right">
                        <span className="margin-sm-left">{form.toHeaderString()}</span>
                    </div>
                )}
                onToggle={() => this.mutateState(
                    s => s.existingForms.mutate(
                        p => p.isExtended = !p.isExtended,
                        p => p.Id === form.Id
                    )
                )}
                delete={{
                    show: this.props.isEditMode,
                    onClick: () => this.handleDeleteFormClick(form.Id)
                }}
                print={{
                    show: !this.props?.isReadOnly,
                    onClick: () => this.handlePrintForm(form.Id)
                }}
                fax={{
                    show: !this.props?.isReadOnly,
                    onClick: () => this.handleOnFax(form)
                }}
                portal={{
                    show: (this.props.patient.IsRegisteredToPortal() || cacheStore.ClinicSettings.EnablePortalInvitationFlowV2) && !this.props?.isReadOnly,
                    onClick: () => this.handleOnPortalSync(form),
                    item: form
                }}
                containerCssClass="margin-xs-bottom"
            >
                <WorkSafeBcPhysicianReportForm
                    ref={ref => this.workSafeBcPhysicianReportForm = ref}
                    isEditMode={this.props.isEditMode}
                    formId={form.Id}
                    worker={WorkSafeBcWorkerModel.fromPatient(this.props.patient)}
                    saveIntervalInSeconds={7}
                    saveUpdatedFields={async updatedFields => await this.saveUpdatedFields(form.Id, updatedFields)}
                />
            </FormContainer>
        );
    }

    private get createNewFormOptions(): KeyValuePair<string, string>[] {
        return [
            new KeyValuePair(GuidHelper.Empty(), cacheStore.GetString("GenerateFromDemographicAndContactInfoLabel")),
            ...this.state.existingForms
                .sortByPropertyDesc(p => p.CreatedDate)
                .map(p => new KeyValuePair(p.Id, p.toSelectingString()))
        ];
    }

    private async onCreateNewForm(): Promise<void> {
        if (this.encounterHasForm())
            return;

        const { formtype, selectedFormId } = this.state.newFormState;
        const response = await Client.postAsync<CreateWorkSafeBcFormResponse>(ServerOperations.Server.WorkSafeBc_CreateForm, {
            FormType: formtype,
            ExistingFormId: selectedFormId === GuidHelper.Empty() ? null : selectedFormId,
            PatientContactId: this.props.patient.IndividualResponse.Contact_id,
            EncounterId: this.props.encounter.Id
        });

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return;
        }

        this.mutateState(s => {
            s.existingForms.push(new WorkSafeBcFormLight({ ...response.data.Form, isExtended: true }));
            s.newFormState = null;
        });

        sessionStore.dispatch(LocalPatientOperations.MedicalNote.WorkSafeBc_UpdateForms, { forms: this.state.existingForms });
    }

    private async saveUpdatedFields(formId: string, updatedFields: Partial<WorkSafeBcPhysicianReportModel>): Promise<void> {
        const response = await Client.postAsync<SaveWorkSafeBcPhysicianReportFormResponse>(
            ServerOperations.Server.WorkSafeBc_SavePhysicianReportForm,
            { Id: formId, UpdatedFields: JSON.stringify(updatedFields) }
        );

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return;
        }

        sessionStore.dispatch(LocalPatientOperations.MedicalNote.WorkSafeBc_HandleSaveForm, { form: response.data.Form });
    }

    private handleDeleteFormClick(id: string): void {
        sessionStore.showPopup({
            title: cacheStore.GetString("WarningLabel"),
            content: cacheStore.GetString("Generic_DeleteWarning"),
            showCancel: true,
            okAction: async () => {
                const response = await Client.postAsync<null>(ServerOperations.Server.WorkSafeBc_DeleteForm, {
                    Id: id,
                    EncounterId: this.props.encounter.Id
                });

                if (!response.isSuccess) {
                    sessionStore.showPopup(response.errors);
                    return;
                }

                sessionStore.dispatch(
                    LocalPatientOperations.MedicalNote.WorkSafeBc_UpdateForms,
                    {
                        forms: this.state.existingForms.filter(p => p.Id !== id)
                    }
                );
            }
        });
    }

    private async handlePrintForm(formId: string): Promise<void> {
        await this.saveForm();

        const response = await Client.postAsync<{ FileName: string }>(ServerOperations.Server.WorkSafeBc_GeneratePhysicianReportFilePdf, {
            FormId: formId
        });

        if (!response.isSuccess) {
            sessionStore.showPopup(response.errors);
            return;
        }

        PrintHelper.ShowPrintPreviewFileName(response.data.FileName);
    }

    private async handleOnFax(form: WorkSafeBcFormLight): Promise<void> {
        await this.saveForm();

        const patientContactId = this.props.patient.IndividualResponse.Contact_id;

        sessionStore.dispatchMain(LocalOperations.MainComponentViewOperations.FaxPrintExportableFile, {
            config: {
                exportableFile: FileTypeWorkSafeBcForm.fromWorkSafeBcForm(form.Id, form.toHeaderString(), form.CreatedDate, patientContactId),
                exportMode: ExportMode.Fax,
                patientContactId,
                isSyncToPortalAfterFaxEnabled: this.props.patient.IsRegisteredToPortal() && (form.hasBeenModifiedSinceLastShared() || form.SharedItemId == null),
                syncToPortalAfterAction: currentPreview => this.handleOnPortalSync(form, currentPreview, true)
            } as IFaxPrintExportableFileConfig
        });
    }

    private async handleOnPortalSync(form: WorkSafeBcFormLight, exportableFile: IExportableFile = null, skipSignature: boolean = false): Promise<void> {
        await this.saveForm();

        sessionStore.dispatch(LocalPatientOperations.MedicalNote.WorkSafeBc_PortalClick, {
            portalShareableItem: form,
            exportableFilePreview: exportableFile ?? this.initFileTypeWorkSafeBcForm(form),
            skipSignature: skipSignature
        });
    }

    private initFileTypeWorkSafeBcForm(form: WorkSafeBcFormLight): FileTypeWorkSafeBcForm {
        const patientContactId = this.props.patient.IndividualResponse.Contact_id;

        return FileTypeWorkSafeBcForm.fromWorkSafeBcForm(
            form.Id,
            form.toHeaderString(),
            form.CreatedDate,
            patientContactId
        );
    }

    private encounterHasForm(): boolean {
        return this.state.existingForms?.any(form => form.EncounterId == this.props.encounter.Id);
    }
}

export { WorkSafeBcController };
